import { FC, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useAuctionPricing } from 'dibs-auctions/exports/hooks';

// components
import { Link } from 'dibs-elements/exports/Link';
import { FormattedMessage } from 'dibs-react-intl';
import { PriceDisplay, FormattedPrice } from 'dibs-buyer-price';
import { PdpSharedAuctionCountdownLazy } from '../PdpSharedAuctionCountdown/PdpSharedAuctionCountdownLazy';
import { PdpSharedAuctionTooltipLazy } from '../PdpSharedAuctionToolTip/PdpSharedAuctionTooltipLazy';

// types
import { PdpSharedAuctionPreview_item$key as Item } from './__generated__/PdpSharedAuctionPreview_item.graphql';

// styles
import styles from './PdpSharedAuctionPreview.scss';

type Props = {
    item: Item;
    currency: string;
    onCountdownFinished: () => void;
    promoCode: ReactNode;
};

const itemFragment = graphql`
    fragment PdpSharedAuctionPreview_item on Item {
        ...PriceDisplay_item @arguments(userId: $userId, isTrade: $isTrade)
        ...useAuctionPricing_item @arguments(page: pdp)
        ...PdpSharedAuctionCountdownLazy_item
    }
`;
export const PdpSharedAuctionPreview: FC<Props> = ({
    item: itemRef,
    currency,
    onCountdownFinished,
    promoCode,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { buyItNowAmount, hasReserve } = useAuctionPricing({
        item,
        currency,
    });

    return (
        <div className={styles.container}>
            <PdpSharedAuctionCountdownLazy item={item} onCountdownFinished={onCountdownFinished} />
            {!hasReserve && (
                <span className={styles.noReserve}>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.noReserve"
                        defaultMessage="No Reserve"
                    />
                    <PdpSharedAuctionTooltipLazy type="reserve" />
                </span>
            )}
            <span className={styles.bid}>
                <FormattedMessage
                    id="PdpSharedAuctionPreview.startingBid"
                    defaultMessage="Starting Bid"
                />
            </span>
            <PriceDisplay currency={currency} item={item} useSvgIcon />
            {buyItNowAmount && (
                <span>
                    <FormattedMessage
                        id="PdpSharedAuctionPreview.currentBid"
                        defaultMessage="{amount} Buy It Now"
                        values={{
                            amount: <FormattedPrice value={buyItNowAmount} currency={currency} />,
                        }}
                    />
                </span>
            )}
            <div className={styles.promoCode}>{promoCode}</div>
            <div className={styles.prompt}>
                <FormattedMessage
                    id="PdpSharedAuctionPreview.prompt"
                    defaultMessage="You can't bid on this item yet. Favorite this item to get updates on this auction. {link}"
                    values={{
                        link: (
                            <Link
                                href="/info/auctions-faq/"
                                target="_blank"
                                className={styles.link}
                            >
                                <FormattedMessage
                                    id="PdpSharedAuctionPreview.learnMore"
                                    defaultMessage="Learn More about Auctions"
                                />
                            </Link>
                        ),
                    }}
                />
            </div>
        </div>
    );
};
